import { useId, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../app/auth/AuthProvider';
import { LiftXLogo } from '../../ui/components/LiftXLogo';
import { CompanyMark } from '../../ui/components/CompanyMark';
import { COMPANY_NAME } from '../../app/appInfo';
import { ROUTES } from '../../ui/navigation/routes';
import styles from './LoginScreen.module.css';

/**
 * The login screen.
 *
 * Appears before anything else in the application. The password typed here is
 * sent in exactly one request — `POST /api/auth/login` — and is never stored,
 * cached, or hashed locally; the server decides, and the resulting session
 * lives in an `HttpOnly` cookie this code cannot read.
 *
 * UI/navigation polish pass: a successful sign-in always lands on the
 * Dashboard, deliberately never restoring whatever URL happened to be in the
 * address bar when the session lapsed (a stale deep link, a route from a
 * previous device/tab, etc.). This is the one and only place a login can
 * succeed in this app — `AuthGate` swaps this screen in for `children`
 * app-wide — so forcing the destination here is the correct architectural
 * level, not a per-screen workaround. `replace: true` so Back from the
 * Dashboard doesn't return to the now-gone login screen. A plain page
 * refresh while already authenticated never runs this code at all — the
 * route is served directly by `AppRoutes` at whatever URL was open, exactly
 * as before.
 */
export function LoginScreen(): React.JSX.Element {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const emailId = useId();
  const passwordId = useId();
  const errorId = useId();

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (submitting) return;

    setSubmitting(true);
    setError(null);
    try {
      const outcome = await login({ email, password });
      if (outcome === 'success') {
        navigate(ROUTES.dashboard, { replace: true });
      } else if (outcome === 'invalid-credentials') {
        // Deliberately generic: does not say which field was wrong.
        setError('Incorrect email or password.');
        setPassword('');
      } else if (outcome === 'email-unverified') {
        // Email-verification enforcement is currently PARKED server-side
        // (login.ts no longer returns this reason — see its own comment),
        // so this branch is unreachable in the active flow. Left in place,
        // not deleted, only because `LoginResult` still declares the
        // outcome (authApi.ts) for a server that re-enables the gate later;
        // falling back to the pending-approval message here is the correct,
        // safe behaviour if that ever happens with a stale frontend build.
        setError('Your account is awaiting administrator approval.');
        setPassword('');
      } else if (outcome === 'pending-approval') {
        // The other deliberately non-generic failure — see login.ts's own
        // comment on why a self-registered account gets a specific message.
        setError('Your account is awaiting administrator approval.');
        setPassword('');
      } else if (outcome === 'network-error') {
        // Distinct from a rejection — telling someone their password is wrong
        // when the request never arrived would send them hunting for the wrong
        // problem. Signing in genuinely requires the server (§11).
        setError('Can’t reach the server. Check your connection and try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className={styles.page}>
      <form className={styles.card} onSubmit={(event) => void handleSubmit(event)} noValidate>
        <div className={styles.logoWrap}>
          <LiftXLogo height={96} />
        </div>

        <h1 className={styles.title}>Sign in</h1>
        <p className={styles.subtitle}>
          <CompanyMark size={14} className={styles.subtitleMark} />
          Personal access · {COMPANY_NAME}
        </p>

        <div className={styles.field}>
          <label className={styles.label} htmlFor={emailId}>
            Email
          </label>
          <input
            id={emailId}
            className={styles.input}
            type="email"
            inputMode="email"
            autoComplete="username"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            disabled={submitting}
            required
            aria-invalid={error !== null}
            aria-describedby={error !== null ? errorId : undefined}
          />
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor={passwordId}>
            Password
          </label>
          <input
            id={passwordId}
            className={styles.input}
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            disabled={submitting}
            required
            aria-invalid={error !== null}
            aria-describedby={error !== null ? errorId : undefined}
          />
        </div>

        {/* role="alert" so the failure is announced without relying on colour
            alone (Phase 0 §16/§20). */}
        {error !== null && (
          <p id={errorId} role="alert" className={styles.error}>
            {error}
          </p>
        )}

        <button type="submit" className={styles.submit} disabled={submitting}>
          {submitting ? 'Signing in…' : 'Sign in'}
        </button>

        <p className={styles.registerPrompt}>
          Don&apos;t have an account?{' '}
          <Link to={ROUTES.register} className={styles.registerLink}>
            Register
          </Link>
        </p>

        <p className={styles.note}>
          Signing in is verified by the LiftX server. An internet connection is
          required to sign in.
        </p>
      </form>
    </div>
  );
}
